/**
 * usePricingPageTracking Hook
 * 
 * Automatically tracks when users visit and leave the pricing page.
 * This is used to trigger abandoned cart emails if they don't purchase.
 * 
 * Usage:
 * import usePricingPageTracking from '../hooks/usePricingPageTracking';
 * 
 * function PricingPage() {
 *   usePricingPageTracking();
 *   // ... rest of component
 * }
 */

import { useEffect, useRef, useState } from 'react';
import { auth } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import emailService from '../services/emailService';

export default function usePricingPageTracking() {
  const [currentUser, setCurrentUser] = useState(null);
  const startTimeRef = useRef(null);
  const trackedRef = useRef(false);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Only track for logged-in users
    if (!currentUser) {
      return;
    }

    // Record when the user entered the page
    startTimeRef.current = Date.now();
    console.log('📊 Pricing page visit started');

    // Cleanup function: Track the visit when user leaves the page
    return () => {
      if (!trackedRef.current && startTimeRef.current && currentUser) {
        const timeSpentSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
        
        // Only track if user spent at least 5 seconds on the page
        if (timeSpentSeconds >= 5) {
          emailService.trackPricingPageVisit(
            currentUser.uid,
            currentUser.email,
            currentUser.displayName,
            timeSpentSeconds
          );
          console.log(`📊 Pricing page visit tracked: ${timeSpentSeconds} seconds`);
          trackedRef.current = true;
        }
      }
    };
  }, [currentUser]);
}
